import { type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { useHasPermission } from '@/auth/auth-store';
import { useFiscalCabinetAvailability } from '@/hooks/use-fiscal-cabinet';
import { useT } from '@/i18n/use-t';
import { cn } from '@/lib/utils';
import { SigningKeyExpiryBanner } from './fiscal-signing-key-banner';

type Tab =
  | 'submissions'
  | 'signatureLog'
  | 'journal'
  | 'objectTree'
  | 'legalEntities'
  | 'signingKeys';

const TAB_LABEL_KEY: Record<Tab, string> = {
  submissions: 'fiscalCabinet.tab.submissions',
  signatureLog: 'fiscalCabinet.tab.signatureLog',
  journal: 'fiscalCabinet.tab.journal',
  objectTree: 'fiscalCabinet.tab.objectTree',
  legalEntities: 'fiscalCabinet.tab.legalEntities',
  signingKeys: 'fiscalCabinet.tab.signingKeys',
};

const TABS: { tab: Tab; to: string; label: string }[] = [
  { tab: 'submissions', to: '/fiscal-cabinet/submissions', label: 'Submissions' },
  { tab: 'signatureLog', to: '/fiscal-cabinet/signature-log', label: 'Signature log' },
  { tab: 'journal', to: '/fiscal-cabinet/journal', label: 'Electronic journal' },
  { tab: 'objectTree', to: '/fiscal-cabinet/object-tree', label: 'Object tree' },
  { tab: 'legalEntities', to: '/fiscal-cabinet/legal-entities', label: 'Legal entities' },
  { tab: 'signingKeys', to: '/fiscal-cabinet/signing-keys', label: 'Signing keys' },
];

/**
 * Shared chrome of the Fiscal cabinet pages (PFC-9): title, the tabs (PFC-21
 * added the electronic journal),
 * and — when the tenant has no Ukraine country pack enabled — the reason the
 * section is empty instead of the page body.
 *
 * `children` receives whether the cabinet is available, so a page does not
 * query data routes that would only answer 404. When it is, a banner warns
 * about signing keys that expire soon or have expired (PFC-13).
 */
export function FiscalCabinetSection({
  children,
}: {
  children: (available: boolean) => ReactNode;
}) {
  const t = useT('admin_ui');
  // A direct link reaches this page without the sidebar's permission check;
  // say so plainly instead of letting the 403 read as a load failure.
  const allowed = useHasPermission('fiscal.cabinet.read');
  const availability = useFiscalCabinetAvailability(allowed);
  const available = availability.data?.available === true;

  return (
    <div className="space-y-5">
      <header>
        <h1 className="text-2xl font-semibold">{t('fiscalCabinet.title', 'Fiscal cabinet')}</h1>
        <p className="text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.subtitle',
            'Registration of stores, PRROs and cashiers with the State Tax Service of Ukraine.',
          )}
        </p>
      </header>

      <nav className="flex flex-wrap items-center gap-1 border-b border-border text-sm">
        {TABS.map(({ tab, to, label }) => (
          <NavLink
            key={tab}
            to={to}
            className={({ isActive }) =>
              cn(
                '-mb-px border-b-2 px-3 py-2 transition-colors',
                isActive
                  ? 'border-primary font-medium text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground',
              )
            }
          >
            {t(TAB_LABEL_KEY[tab], label)}
          </NavLink>
        ))}
      </nav>

      {!allowed ? (
        <p className="rounded-md border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.noAccess',
            'You do not have access to the fiscal cabinet. Ask an administrator for the fiscal cabinet read permission.',
          )}
        </p>
      ) : availability.isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading', 'Loading…')}</p>
      ) : availability.isError ? (
        <p className="text-sm text-destructive">
          {t('fiscalCabinet.loadError', 'Failed to load the fiscal cabinet.')}
        </p>
      ) : !available ? (
        <p className="rounded-md border border-border bg-card px-4 py-6 text-sm text-muted-foreground">
          {t(
            'fiscalCabinet.unavailable',
            'The fiscal cabinet works only with the Ukraine country pack. Enable it under Country packs to use this section.',
          )}
        </p>
      ) : (
        <>
          <SigningKeyExpiryBanner />
          {children(available)}
        </>
      )}
    </div>
  );
}

/** The empty panel every list and placeholder uses. */
export function EmptyPanel({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-md border border-border bg-card px-4 py-10 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

/** Previous / next for a paged list; hidden when everything fits on one page. */
export function Pager({
  page,
  pageSize,
  total,
  onPage,
}: {
  page: number;
  pageSize: number;
  total: number;
  onPage: (page: number) => void;
}) {
  const t = useT('admin_ui');
  if (total <= pageSize) return null;
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);
  return (
    <div className="flex items-center justify-end gap-3 text-sm text-muted-foreground">
      <span>
        {t('fiscalCabinet.pager.summary', '{from}–{to} of {total}')
          .replace('{from}', String(from))
          .replace('{to}', String(to))
          .replace('{total}', String(total))}
      </span>
      <button
        type="button"
        className="rounded-md border border-border px-3 py-1 disabled:opacity-50"
        disabled={page <= 1}
        onClick={() => onPage(page - 1)}
      >
        {t('fiscalCabinet.pager.previous', 'Previous')}
      </button>
      <button
        type="button"
        className="rounded-md border border-border px-3 py-1 disabled:opacity-50"
        disabled={to >= total}
        onClick={() => onPage(page + 1)}
      >
        {t('fiscalCabinet.pager.next', 'Next')}
      </button>
    </div>
  );
}
